/**
 * Period closing orchestration — runs after daily closing catch-up.
 * Once-per-period (week → month → quarter → year), never daily re-runs.
 */

import periodClosingService from '../../services/periodClosingService';
import { getCurrentBusinessId } from '../../utils/sharedCore';
import { flushPendingStorageWrites } from '../../infrastructure/storage/businessPayloadWrite';

let periodClosingRunPromise = null;

/**
 * Automatic catch-up for due period closings (idempotent).
 */
export async function runAutomaticPeriodClosingCheck(closedBy = 'system') {
  if (periodClosingRunPromise) {
    return periodClosingRunPromise;
  }

  periodClosingRunPromise = (async () => {
    try {
      const businessId = getCurrentBusinessId({ allowFallback: false });
      if (!businessId) {
        return { processed: [], errors: ['No business'], skipped: [] };
      }

      const result = await periodClosingService.checkAndPerformAutomaticPeriodClosings(
        closedBy,
        businessId
      );

      if (result.processed?.length > 0) {
        console.log(
          `✅ [PERIOD CLOSING] ${result.processed.length} Periode(n) abgeschlossen:`,
          result.processed.map((p) => `${p.type}:${p.periodKey}`).join(', ')
        );
        await flushPendingStorageWrites();
      }

      if (result.errors?.length > 0) {
        console.warn('⚠️ [PERIOD CLOSING] Fehler:', result.errors);
      }

      return result;
    } finally {
      periodClosingRunPromise = null;
    }
  })();

  return periodClosingRunPromise;
}

/**
 * Whether cloud sync/upload should pause until a manager reviews period closings.
 */
export function isPeriodReviewBlockingSync(businessId = null) {
  const bid = businessId || getCurrentBusinessId({ allowFallback: false });
  if (!bid) return false;
  return periodClosingService.hasPendingReview(bid);
}

export function getPendingPeriodReviews(businessId = null) {
  const bid = businessId || getCurrentBusinessId({ allowFallback: false });
  if (!bid) return [];
  return periodClosingService.getPendingReviews(bid);
}

export async function approvePeriodClosingReview(
  type,
  periodKey,
  { reviewedBy = 'manager', businessId = null } = {}
) {
  const result = await periodClosingService.approvePeriodClosing(
    type,
    periodKey,
    reviewedBy,
    businessId
  );
  if (result.success) {
    await flushPendingStorageWrites();
  }
  return result;
}
